import {
  Box,
  BoxProps,
  HStack,
  Heading,
  IconButton,
  Image,
  LinkOverlay,
  Text,
  VStack
} from "@chakra-ui/react"
import { forwardRef, useMemo } from "react"
import { Link as ReactRouterLink } from "react-router-dom"

import RevealOnGroupHover from "@pathwright/ux/src/components/style/RevealOnGroupHover"
import Pathicon from "../../../pathicon/Pathicon"
import useSoftArchiveRegistration from "../../useSoftArchiveRegistration"
import { getRegistrationPathLink } from "../../utils"
import NestedPaths from "./NestedPaths"
import PathCardAction from "./PathCardAction"
import PathCardRegistrationControls from "./PathCardRegistrationControls"
import { PathCardProps } from "./types"

type PathCardListItemProps = PathCardProps & BoxProps

const PathCardListItem = forwardRef<HTMLDivElement, PathCardListItemProps>(
  (
    {
      registration,
      parentRegistration,
      nestedRegistrations,
      enableLink,
      ...boxProps
    },
    ref
  ) => {
    const resource = registration.resource!
    const pathLink = useMemo(() => getRegistrationPathLink(registration), [])

    // Using image width also for determining offset of nested paths.
    const imgWidth = "4em"

    const [softArchive] = useSoftArchiveRegistration(registration)
    // You can only soft unarchive a child registration when it's parent
    // is not soft archived.
    const canSoftUnarchive =
      !!registration.softArchivedTime &&
      (!parentRegistration || !parentRegistration.softArchivedTime)

    return (
      <Box
        ref={ref}
        // Using "group" className to allow revealing of PathCardRegistrationControls
        // on hover of the PathCardListItem.
        className="PathCardListItem group"
        w="100%"
        pos="relative"
        {...boxProps}
        // Eh, overriding, which might not be best.
        borderRadius="md"
      >
        {enableLink && (
          <LinkOverlay
            className="PathCardListItem__link-overlay"
            as={ReactRouterLink}
            to={pathLink}
            w="100%"
            textDecoration="none"
          />
        )}
        <VStack
          w="100%"
          alignItems="flex-start"
          spacing={1}
          sx={{
            [".PathProgressBar"]: {
              fontSize: ".6em"
            }
          }}
        >
          <HStack w="100%">
            <Image
              src={resource.cover_image}
              w={imgWidth}
              h="3em"
              borderRadius="md"
              alignSelf="flex-start"
            />
            <VStack
              spacing={1}
              align="flex-start"
              justify="center"
              flexGrow={1}
            >
              <Heading
                as="h2"
                className="theme-heading theme-heading-bold"
                fontSize="sm"
                color="blackAlpha.900"
                noOfLines={2}
                m={0}
              >
                {resource.name}
              </Heading>
              {
                // Moderators and Teachers should see list of Cohort members.
                [10, 15].includes(registration.role!) && (
                  <HStack spacing={1}>
                    {/* <GroupAvatars
                    groupId={registration?.group?.id as number}
                    showGroupName={false}
                    noOfLines={1}
                    // @ts-ignore
                    fontColor="blackAlpha.900"
                  /> */}
                    <Pathicon
                      icon="group"
                      size=".85em"
                      color="blackAlpha.900"
                    />
                    <Text
                      as="span"
                      noOfLines={1}
                      fontSize="xs"
                      color="blackAlpha.900"
                    >
                      {registration?.group?.name}
                    </Text>
                  </HStack>
                )
              }

              <PathCardAction
                registration={registration}
                colorScheme="blackAlpha"
                size="xs"
              />
            </VStack>
          </HStack>

          {!!nestedRegistrations?.length && (
            <Box ml={`calc(${imgWidth} + var(--chakra-space-2))`}>
              <NestedPaths
                nestedRegistrations={nestedRegistrations}
                fontSize=".5em"
              />
            </Box>
          )}

          <RevealOnGroupHover
            as={HStack}
            spacing={1}
            pos="absolute"
            top={1}
            right={1}
          >
            {canSoftUnarchive && (
              <IconButton
                aria-label="Add to Home Screen"
                icon={
                  <Pathicon
                    icon="sidebar-bottom"
                    transform="rotate(90deg)"
                    size="1em"
                  />
                }
                colorScheme="blackAlpha"
                size="xs"
                cursor="pointer"
                onClick={() => softArchive()}
              />
            )}

            <PathCardRegistrationControls
              registration={registration}
              parentRegistration={parentRegistration}
              renderPortal
              triggerProps={{
                // Tricky to type props correctly to avoid requiring "aria-label" here.
                "aria-label": "Path Card Controls",
                colorScheme: "blackAlpha"
              }}
            />
          </RevealOnGroupHover>
        </VStack>
      </Box>
    )
  }
)

export default PathCardListItem
